import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import websocketManager, { ConnectionState } from '@/lib/websocket-manager';
import { TaxonomyRun } from './useTaxonomyRun';

interface TaxonomyWebSocketHookResult {
  /**
   * Current taxonomy run data received from WebSocket
   */
  runData: TaxonomyRun | null;
  
  /**
   * Current connection state of the WebSocket
   */
  connectionState: ConnectionState;
  
  /**
   * Latest step reported by the WebSocket
   */
  currentStep: string;
  
  /**
   * Error message if any occurred during WebSocket communication
   */
  error: string | null;
}

/**
 * React hook for receiving real-time taxonomy updates via WebSocket
 * 
 * @param kbId Knowledge base ID
 * @returns Hook state containing the latest run data and connection state
 */
export function useWebSocketTaxonomy(kbId: string): TaxonomyWebSocketHookResult {
  const [runData, setRunData] = useState<TaxonomyRun | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.DISCONNECTED);
  const [currentStep, setCurrentStep] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const wsUrl = useRef<string>('');

  useEffect(() => {
    if (!kbId) return;

    // Use VITE_WS_URL if set, otherwise fall back to same-origin WebSocket
    const wsBaseUrl = import.meta.env.VITE_WS_URL;
    if (wsBaseUrl) {
      wsUrl.current = `${wsBaseUrl}/ws/taxonomy/${kbId}/`;
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      wsUrl.current = `${protocol}//${host}/ws/taxonomy/${kbId}/`;
    }

    // Connect to the WebSocket endpoint
    websocketManager.connect(wsUrl.current);

    // Subscribe to connection state changes
    const unsubConnectionState = websocketManager.onConnectionStateChange((state) => {
      setConnectionState(state);
      
      // Reset error when connection is established
      if (state === ConnectionState.CONNECTED) {
        setError(null);
      }
    });

    // Subscribe to taxonomy status updates
    const unsubTaxonomyStatus = websocketManager.subscribe('taxonomy_status', (data) => {
      console.log('WebSocket taxonomy update:', data);
      
      if (data) {
        // Update local state
        setRunData({
          run_id: data.run_id,
          status: data.status,
          progress: data.progress || 0,
          total_chunks_processed: data.total_chunks_processed || 0,
          intents_discovered: data.intents_discovered || 0,
          error_log: data.error_log || '',
          created_at: data.created_at || new Date().toISOString(),
          completed_at: data.completed_at || null,
          step: data.step,
          chunks_in_step: data.chunks_in_step,
        });

        // Set current pipeline step if available
        if (data.step) {
          setCurrentStep(data.step);
        }

        // When a run completes or fails, invalidate the query cache to refresh data
        if (data.status === 'completed' || data.status === 'failed') {
          queryClient.invalidateQueries({ queryKey: ['taxonomy-latest', kbId] });
          queryClient.invalidateQueries({ queryKey: ['taxonomy-result', kbId, data.run_id] });
          queryClient.invalidateQueries({ queryKey: ['taxonomy-graph', kbId, data.run_id] });
          // Also invalidate GenClair standalone queries
          queryClient.invalidateQueries({ queryKey: ['genclair-run', data.run_id] });
          queryClient.invalidateQueries({ queryKey: ['genclair-run-result', data.run_id] });
          queryClient.invalidateQueries({ queryKey: ['genclair-run-graph', data.run_id] });
          queryClient.invalidateQueries({ queryKey: ['genclair-runs'] });
        }
      }
    });

    return () => {
      unsubConnectionState();
      unsubTaxonomyStatus();
      websocketManager.disconnect();
    };
  }, [kbId, queryClient]);

  return {
    runData,
    connectionState,
    currentStep,
    error
  };
}

export default useWebSocketTaxonomy;
